/**
 * Turning classified click candidates into concrete results: POIs (via the per-type handlers),
 * drawer-entity POIs, and the `OverlappingFeatureAction`s used for router navigation and the
 * POI dialog / stacked footer.
 */
import {
  DrawerEntityPoiType,
  FeatureWithEntityType,
  MapboxFeature,
  MapDrawerEntityForPoi,
  OverlappingFeatureAction,
  StandardPoi,
  StandardPoiType,
} from 'features/mapping/mapping.types';
import { ExternalGeoEvent, GeoEventEvacZoneStatus } from 'shared/types';
import { RouterEntityDrawer } from '../../../constants';
import {
  getPolicyIndex,
  MapClickTargetType,
  ROUTER_ENTITY_DRAWER_PRIORITY,
} from './priority';
import {
  ENTITY_DRAWER_POI_HANDLERS,
  POI_FEATURE_COMPARATORS,
  POI_HANDLERS,
  ROUTER_ENTITY_FEATURE_COMPARATORS,
  ROUTER_ENTITY_PATH_RESOLVERS,
} from './constants';
import { deduplicateFeatures } from './interactiveFeatures';

/**
 * Order POI features by type policy, then within a type by its comparator. Every downstream sort
 * is stable, so this within-type order survives into the dialog / stacked footer.
 */
export const sortPoiFeatures = (
  features: FeatureWithEntityType[],
): FeatureWithEntityType[] =>
  [...features].sort((a, b) => {
    const byType =
      getPolicyIndex(a.entityType as MapClickTargetType) -
      getPolicyIndex(b.entityType as MapClickTargetType);
    if (byType !== 0) return byType;
    const comparator = POI_FEATURE_COMPARATORS[a.entityType as StandardPoiType];
    return comparator ? comparator(a.feature, b.feature) : 0;
  });

/**
 * For each POI candidate, invoke the type handler with a single-feature array,
 * in `sortPoiFeatures` order.
 */
export const collectPois = (
  candidates: FeatureWithEntityType[],
  activeEvacZones: GeoEventEvacZoneStatus[],
  externalGeoEvents: ExternalGeoEvent[],
): StandardPoi[] =>
  sortPoiFeatures(candidates).flatMap(({ feature, entityType }) => {
    const handler = POI_HANDLERS[entityType as StandardPoiType];
    return (
      handler?.(
        [feature] as MapboxFeature<unknown>[],
        activeEvacZones,
        externalGeoEvents,
      ) ?? []
    );
  });

/**
 * For each Drawer Entity POI type with a handler, collect features of that type,
 * run its handler, and aggregate all returned MapDrawerEntityForPOIs.
 */
export const collectDrawerEntityPois = (
  candidates: FeatureWithEntityType[],
): MapDrawerEntityForPoi[] => {
  const entityDrawerPois: MapDrawerEntityForPoi[] = [];

  for (const key of Object.keys(ENTITY_DRAWER_POI_HANDLERS)) {
    const entityDrawerPoiType = key as DrawerEntityPoiType;

    const featuresOfType = candidates
      .filter(({ entityType }) => entityType === entityDrawerPoiType)
      .map(({ feature }) => feature);

    if (featuresOfType.length) {
      const handler = ENTITY_DRAWER_POI_HANDLERS[entityDrawerPoiType];
      entityDrawerPois.push(...handler(featuresOfType));
    }
  }

  return entityDrawerPois;
};

/**
 * Resolve router entity candidates into OverlappingFeatureActions.
 * Each candidate feature is resolved individually so that multiple overlapping
 * polygon features of the same entity type each produce their own action.
 * Results are sorted by entity type priority.
 */
export const resolveRouterEntityActions = (
  candidates: FeatureWithEntityType[],
): OverlappingFeatureAction[] => {
  const dedupedCandidates = deduplicateFeatures(candidates);
  // Sort same-type candidates by entity-specific comparator, different types by priority
  const sorted = [...dedupedCandidates].sort((a, b) => {
    if (a.entityType === b.entityType) {
      const comparator =
        ROUTER_ENTITY_FEATURE_COMPARATORS[a.entityType as RouterEntityDrawer];
      return comparator ? comparator(a.feature, b.feature) : 0;
    }
    const aPriority =
      ROUTER_ENTITY_DRAWER_PRIORITY[a.entityType as RouterEntityDrawer] ??
      Number.MAX_SAFE_INTEGER;
    const bPriority =
      ROUTER_ENTITY_DRAWER_PRIORITY[b.entityType as RouterEntityDrawer] ??
      Number.MAX_SAFE_INTEGER;
    return aPriority - bPriority;
  });

  const actions: OverlappingFeatureAction[] = [];

  for (const candidate of sorted) {
    const entityType = candidate.entityType as RouterEntityDrawer;
    const resolvePath = ROUTER_ENTITY_PATH_RESOLVERS[entityType];
    const path = resolvePath([candidate.feature]);
    if (path) {
      actions.push({ category: 'routerEntity', entityType, path });
    }
  }

  return actions;
};

/**
 * Map a pre-filtered POI into its OverlappingFeatureAction. An externalGeoEvent becomes a
 * routerEntity action targeting /s/:id so it navigates to a card instead of the POI dialog.
 */
export const resolvePoiAction = (
  poi: StandardPoi,
): OverlappingFeatureAction => {
  // Read window.location.search directly: it already reflects any synchronous setMapMode('default')
  // URL change; useLocation would be stale until the next render.
  const { search } = window.location;
  return poi.type === 'externalGeoEvent' && poi.metadata
    ? {
        category: 'routerEntity',
        entityType: 'externalGeoEvent',
        path: `/s/${poi.metadata.id}${search}`,
      }
    : { category: 'poi', poi };
};
